/**
 * "Fill range…": start a background fill of many days on the server and follow
 * it until it is done. The server keeps going if the dialog closes; reopening
 * picks up the same run (the server hands back a fill that is still running).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import rosterAssignmentsApi, { FillRangeProgress } from "../../../../../services/roster-assignments-api";

const POLL_MS = 2000;

/**
 * Wait for a fill the server started (after "Update schedule" or its undo),
 * reporting progress; resolves when it is done, failed or no longer known.
 */
export async function waitForFill(jobId: string, onProgress?: (progress: FillRangeProgress) => void): Promise<FillRangeProgress | null> {
	for (let i = 0; i < 300; i += 1) {
		try {
			const progress = await rosterAssignmentsApi.getFillRange(jobId);
			onProgress?.(progress);
			if (progress.status !== "running") return progress;
		} catch {
			return null;
		}
		await new Promise((resolve) => window.setTimeout(resolve, POLL_MS));
	}
	return null;
}

export function useFillRange(locationId: number, onDone: () => void | Promise<void>) {
	const [progress, setProgress] = useState<FillRangeProgress | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [starting, setStarting] = useState(false);
	const timer = useRef<number | null>(null);
	const doneRef = useRef(onDone);
	doneRef.current = onDone;

	const stop = useCallback(() => {
		if (timer.current !== null) {
			window.clearTimeout(timer.current);
			timer.current = null;
		}
	}, []);

	const follow = useCallback(
		(jobId: string) => {
			stop();
			const tick = () => {
				rosterAssignmentsApi
					.getFillRange(jobId)
					.then((next) => {
						setProgress(next);
						if (next.status === "running") {
							timer.current = window.setTimeout(tick, POLL_MS);
						} else {
							void doneRef.current();
						}
					})
					.catch((err: { response?: { status?: number } }) => {
						// The server keeps a run's progress in memory: a restart (a deploy) forgets it,
						// and the fill stopped with it. Anything else is a blip: try again.
						if (err?.response?.status === 404) {
							setProgress((last) => (last ? { ...last, status: "failed", current: null } : last));
							setError("The fill was interrupted (the server restarted). Days already filled stay filled — fill again to finish.");
							void doneRef.current();
							return;
						}
						timer.current = window.setTimeout(tick, POLL_MS * 2);
					});
			};
			tick();
		},
		[stop]
	);

	useEffect(() => stop, [stop]);

	const start = useCallback(
		async (from: string, to: string, includeCluster: boolean) => {
			setError(null);
			setStarting(true);
			try {
				const job = await rosterAssignmentsApi.startFillRange(from, to, locationId, includeCluster);
				setProgress(job);
				follow(job.jobId);
			} catch (err) {
				console.error("Range fill failed to start:", err);
				setError("The fill could not start. Try again in a moment.");
			} finally {
				setStarting(false);
			}
		},
		[locationId, follow]
	);

	return useMemo(() => ({ progress, error, starting, start, running: progress?.status === "running" }), [progress, error, starting, start]);
}

export type FillRange = ReturnType<typeof useFillRange>;
